import {
  BRIDGE_SOURCE,
  HOST_APPS,
  PROTOCOL_VERSION,
  type HostApp,
  type HostToNovaMessage,
  type NovaHostContext,
  type NovaHostContextPatch,
  type NovaToHostMessage,
} from './types';

export type MessageParseResult<TMessage> =
  | { status: 'valid'; message: TMessage }
  | { status: 'unknown' }
  | { status: 'invalid'; type?: string };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

function isBoolean(value: unknown): value is boolean {
  return typeof value === 'boolean';
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function hasOnlyOptionalString(value: Record<string, unknown>, key: string): boolean {
  return value[key] === undefined || isString(value[key]);
}

export function isHostApp(value: unknown): value is HostApp {
  return isString(value) && (HOST_APPS as readonly string[]).includes(value);
}

export function isNovaHostContext(value: unknown): value is NovaHostContext {
  return isRecord(value) && isHostApp(value.hostApp) && hasOnlyOptionalString(value, 'accountUuid');
}

export function isNovaHostContextPatch(value: unknown): value is NovaHostContextPatch {
  if (!isRecord(value)) return false;
  return value.accountUuid === undefined || value.accountUuid === null || isString(value.accountUuid);
}

function isVisibility(value: unknown): boolean {
  return isRecord(value) && isBoolean(value.visible) && isBoolean(value.focused);
}

function isObjectPayload(value: unknown): boolean {
  return isRecord(value);
}

const MAX_LAUNCHER_INSET = 64;

function isLauncherInset(value: unknown): value is { top: number; right: number } {
  return (
    isRecord(value) &&
    isFiniteNumber(value.top) &&
    value.top >= 0 &&
    value.top <= MAX_LAUNCHER_INSET &&
    isFiniteNumber(value.right) &&
    value.right >= 0 &&
    value.right <= MAX_LAUNCHER_INSET
  );
}

function parseEnvelope(value: unknown): { type: string; payload: unknown } | null {
  if (
    !isRecord(value) ||
    value.source !== BRIDGE_SOURCE ||
    value.protocolVersion !== PROTOCOL_VERSION ||
    !isString(value.type) ||
    !('payload' in value)
  ) {
    return null;
  }
  return { type: value.type, payload: value.payload };
}

export function parseNovaToHostMessage(value: unknown): MessageParseResult<NovaToHostMessage> {
  const envelope = parseEnvelope(value);
  if (!envelope) return { status: 'invalid' };
  const { type, payload } = envelope;

  if (type === 'nova:ready') {
    const valid =
      isRecord(payload) &&
      isString(payload.embedVersion) &&
      Array.isArray(payload.capabilities) &&
      payload.capabilities.every(isString);
    return valid ? { status: 'valid', message: value as NovaToHostMessage } : { status: 'invalid', type };
  }
  if (type === 'nova:state') {
    const valid =
      isRecord(payload) &&
      isBoolean(payload.enabled) &&
      isBoolean(payload.open) &&
      ['panel', 'panel-sidebar', 'fullscreen'].includes(String(payload.dimension)) &&
      isBoolean(payload.hasNotifications) &&
      (payload.launcher === undefined ||
        (isRecord(payload.launcher) &&
          isFiniteNumber(payload.launcher.width) &&
          payload.launcher.width > 0 &&
          isFiniteNumber(payload.launcher.height) &&
          payload.launcher.height > 0 &&
          (payload.launcher.inset === undefined || isLauncherInset(payload.launcher.inset))));
    return valid ? { status: 'valid', message: value as NovaToHostMessage } : { status: 'invalid', type };
  }
  if (type === 'nova:launcher-move') {
    const valid = isRecord(payload) && isFiniteNumber(payload.deltaX) && isBoolean(payload.persist);
    return valid ? { status: 'valid', message: value as NovaToHostMessage } : { status: 'invalid', type };
  }
  if (type === 'nova:error') {
    const valid =
      isRecord(payload) &&
      (payload.code === 'unauthenticated' || payload.code === 'internal') &&
      (payload.message === undefined || isString(payload.message));
    return valid ? { status: 'valid', message: value as NovaToHostMessage } : { status: 'invalid', type };
  }
  return { status: 'unknown' };
}

export function parseHostToNovaMessage(value: unknown): MessageParseResult<HostToNovaMessage> {
  const envelope = parseEnvelope(value);
  if (!envelope) return { status: 'invalid' };
  const { type, payload } = envelope;

  if (type === 'host:init') {
    const valid =
      isRecord(payload) &&
      isString(payload.loaderVersion) &&
      isHostApp(payload.hostApp) &&
      isNovaHostContext(payload.context) &&
      payload.context.hostApp === payload.hostApp &&
      isVisibility(payload.visibility) &&
      isBoolean(payload.open);
    return valid ? { status: 'valid', message: value as HostToNovaMessage } : { status: 'invalid', type };
  }
  if (type === 'host:context') {
    const valid = isRecord(payload) && isNovaHostContextPatch(payload.patch);
    return valid ? { status: 'valid', message: value as HostToNovaMessage } : { status: 'invalid', type };
  }
  if (type === 'host:open' || type === 'host:close' || type === 'host:toggle') {
    return isObjectPayload(payload)
      ? { status: 'valid', message: value as HostToNovaMessage }
      : { status: 'invalid', type };
  }
  if (type === 'host:visibility') {
    return isVisibility(payload)
      ? { status: 'valid', message: value as HostToNovaMessage }
      : { status: 'invalid', type };
  }
  return { status: 'unknown' };
}

export function parseMessageEvent<TMessage>({
  event,
  expectedOrigin,
  expectedSource,
  parse,
}: {
  event: Pick<MessageEvent, 'origin' | 'source' | 'data'>;
  expectedOrigin: string;
  expectedSource: MessageEventSource | null;
  parse: (value: unknown) => MessageParseResult<TMessage>;
}): MessageParseResult<TMessage> {
  if (event.origin !== expectedOrigin || event.source !== expectedSource) {
    return { status: 'invalid' };
  }
  return parse(event.data);
}

export function mergeHostContextPatch(context: NovaHostContext, patch: NovaHostContextPatch): NovaHostContext {
  const next = { ...context };
  if ('accountUuid' in patch) {
    if (patch.accountUuid === null || patch.accountUuid === undefined) delete next.accountUuid;
    else next.accountUuid = patch.accountUuid;
  }
  return next;
}
